import { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import UserContext from '../../context/UserContext';
import UserCard from '../sidebar/UserCard';
import HeaderWrite from './HeaderWrite';
import PopularSearchList from './PopularSearchList';
import { resolveUserProfileImg } from '../../util/ProfileUtil';
import UserApi from '../../api/UserApi';
import useUnreadChatCount from '../../util/useUnreadChatCount';

const getCommunityMenus = (isMyPage, isMobile) => {
    return isMyPage ? [
        { to: '/mypage/info', text: '내정보' },
        { to: '/mypage/group', text: '그룹관리' },
        { to: '/mypage/order', text: '주문내역' },
        { to: '/mypage/point', text: '포인트내역' },
        { to: '/mypage/post', text: '게시글내역' },
        { to: '/mypage/expert', text: '전문가서비스' },
        { to: '/mypage/membership', text: '멤버쉽' },
    ] : isMobile ? [
        { to: '/community', text: '커뮤니티' },
        { to: '/expert', text: '전문가 서비스' },
        { to: '/consulting', text: '마케팅 서비스' },
    ] : [
        { to: '/free-community?postType=PT011', text: '자유 광장' },
        { to: '/anon-community?postType=PT010', text: '익명 광장' },
        { to: '/information?postType=PT020', text: '정보 광장' },
        { to: '/qna?postType=PT030', text: '질문 답변' },
        { to: '/program?postType=PT040', text: '프로그램' }, 
        { to: '/notice?postType=PT060',    text: '공지' },
        { to: '/membership', text: '멤버쉽' },
    ]
}

const linkActiveClass = to => {
    if (to === '/placeRank') {
        if ([
            '#/placeRank', '#/shoppingRank', '#/placeOnce', '#/shoppingOnce', '#/keywordsOnce', '#/visitorCheck', '#/searchAmount', '#/relateKeywords'
        ].includes(window.location.hash)) return 'active';
    }
    if (window.location.hash === `#${to}`) return 'active';

    const menuPath = to.split('?')[0];
    const hashPath = window.location.hash.replace(/^#/, '').split('?')[0];
    if (menuPath !== '/' && (hashPath === menuPath || hashPath.startsWith(`${menuPath}/`))) {
        return 'active';
    }
    return '';
}

const subLinkActiveClass = to => {
    if (window.location.hash === `#${to}`) return 'active';
    return '';
};

const CHAT_SERVICE_URL = process.env.REACT_APP_CHAT_SERVICE_URL || 'http://localhost:5173';

const GNB_ITEMS = [
    { to: "/community", label: "커뮤니티" },
    { to: "/expert", label: "전문가 서비스" },
    { to: "/consulting", label: "마케팅 서비스" },
    {
        to: "/marketingCompare",
        label: "마케팅 견적비교",
        linkClassName: "header-gnb-spacial",
    },
];

const getGnbActiveClass = (to, pathname) => {
    if (to === "/marketingCompare") {
        return pathname.startsWith("/marketingCompare") ? "active" : "";
    }
    if (to === "/expert") {
        return pathname.startsWith("/expert") ? "active" : "";
    }
    if (to === "/community") {
        const communityPaths = [
            "/community",
            "/free-community",
            "/anon-community",
            "/information",
            "/qna",
            "/program",
            "/notice",
        ];
        return communityPaths.some((p) => pathname.startsWith(p)) ? "active" : "";
    }
    if (to === "/consulting") {
        return pathname.startsWith("/consulting") ? "active" : "";
    }
    return pathname.startsWith(to) ? "active" : "";
};

const CustomLink = ({ to = '/', className = '', children }) => {
    if (to.startsWith("/")) return <Link to={to} className={className}>{children}</Link>
    return <a href={to} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>
}

const Nav = ({ type = 'pc', handleNavLinkClick }) => {

    const navigate = useNavigate();

    const [navList, setNavList] = useState([
        { to: '/', text: '광장' },
        { to: '/community', text: '커뮤니티', children : [
            { to: '/free-community?postType=PT011', text: '자유 광장' },
            { to: '/anon-community?postType=PT010', text: '익명 광장' },
            { to: '/community?postType=PT012', text: '셀러 광장' }, // 온라인 판매자 게시판
        ]},
        { to: '/information', text: '정보광장', children : [
            { to: '/information?postType=PT020', text: '정보 공유' },
            { to: '/information?postType=PT021', text: '셀러 정보 공유' },
        ]}, 
        { to: '/qna', text: '질문광장', children : [
            { to: '/qna?postType=PT030', text: '질문 답변' },
            { to: '/qna?postType=PT031', text: '셀러 질문 답변' },
        ]},
        { to: '/expert', className: 'red bold', text: '전문가 서비스'},
        { to: '/consulting', className: 'red bold', text: '마케팅 서비스'},
        { to: '/openads', className: 'red bold', text: '자유홍보광고'},
        { to: 'https://dashboard.mbizsquare.com', text: '광고분석'},
        { to: '/placeRank', text: '순위 조회', children: [
            { to: '/placeRank', text: '플레이스 순위 체크' },
            { to: '/shoppingRank', text: '쇼핑 순위 체크' },
            // { to: '/coupangRank', text: '쿠팡 순위 체크' },
            { to: '/placeOnce', text: '플레이스 1회 순위체크' },
            { to: '/shoppingOnce', text: '쇼핑 1회 순위체크' },
            { to: '/keywordsOnce', text: '대표 키워드 조회' },
            { to: '/visitorCheck', text: '블로그 방문자 수 조회' },
            { to: '/searchAmount', text: '키워드 검색량 조회' },
            { to: '/relateKeywords', text: '연관 키워드 조회' },
        ]},
        { to: '/program', text: '프로그램 센터' },
        { to: '/order', text: '회원상품결제' },
        { to: '/notice', text: '공지', children : [
            { to: '/notice?postType=PT060', text: '광장 공지'},
            { to: '/notice?postType=PT061', text: 'N블로그 공지'},
            { to: '/notice?postType=PT062', text: 'N플레이스 공지'},
            { to: '/notice?postType=PT063', text: 'N통합검색 공지'},
        ]}
    ]);
    
    const handleTitleClick = idx => {
        const newNavList = [...navList];
        newNavList[idx].isOpen = !newNavList[idx].isOpen;
        setNavList(newNavList);
    }

    if (type === 'pc') {
        return navList.map(({to, text, children=[], className=''}, idx) => (
            <li key={to} className={(idx > 0 ? 'gnb-category ' : '') + linkActiveClass(to)} onClick={handleNavLinkClick}>
                <CustomLink to={to} className={className}>{text}</CustomLink>
                {children.length > 0 && <ul className='depth-menu'>
                    { children.map(c => (
                        <li key={c.to} onClick={handleNavLinkClick}>
                            <CustomLink to={c.to}>{c.text}</CustomLink>
                        </li>
                    ))}
                </ul>}
                
            </li>
        ));
    }

    return navList.map(({to, text, isOpen=false, children=[], className=''}, idx) => (
        <li className={linkActiveClass(to)}>
            <div className={`mobile-gnb-title ${isOpen ? 'open' : ''}`}>
                <p  className={className}
                    onClick={() => {
                    handleNavLinkClick();
                    navigate(to);
                }}>{text}</p>
                {children.length > 0 && <i className="icon icon-arrow-down" onClick={() => handleTitleClick(idx)}></i>}
            </div>
            {(isOpen && children.length > 0) && <ul className={'mobile-depth-menu open'} style={{ height: 'auto' }}>
                { children.map(c => (
                    <li key={c.to} className={subLinkActiveClass(c.to)} onClick={handleNavLinkClick}>
                        <CustomLink to={c.to}>{c.text}</CustomLink>
                    </li>
                ))}
            </ul>}
        </li>
    ));
}


const MobileGnbOpenBtn = ({ onClick }) => (
    <div className="header-mobile-tool">
        <button type="button" className="icon_btn" id="gnbOpenBtn" onClick={onClick}>
            <i className="icon icon-black icon-bugger"></i>
        </button>
    </div>
);

const MobileHeaderTool = ({ isLoggedIn, onLogout }) => {
    const location = useLocation();
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const profileDropdownRef = useRef(null);

    const handleProfileMenuToggle = () => {
        setIsProfileDropdownOpen(prev => !prev);
    };

    const handleProfileDropdownLinkClick = () => {
        setIsProfileDropdownOpen(false);
    };

    const handleLogoutClick = (e) => {
        e?.preventDefault?.();
        onLogout(e);
        setIsProfileDropdownOpen(false);
    };

    useEffect(() => {
        setIsProfileDropdownOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!isProfileDropdownOpen) return;

        const handleClickOutside = (e) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
                setIsProfileDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isProfileDropdownOpen]);

    return (
    <div className="header-mobile-tool" ref={profileDropdownRef}>
        {/* <button type="button" className="icon_btn">
            <i className="icon icon-black icon-search-lg"></i>
        </button> */}
        {isLoggedIn && (
            <>
                <button type="button" className="icon_btn profileMenuBtn" onClick={handleProfileMenuToggle}>
                    <i className="icon icon-user"></i>
                </button>
                <ul className={`header-login-dropdown${isProfileDropdownOpen ? ' open' : ''}`}>
                    <li><Link to="/mypage/info" onClick={handleProfileDropdownLinkClick}>마이페이지</Link></li>
                    <li><Link to="/mypage/post" onClick={handleProfileDropdownLinkClick}>게시글 내역</Link></li>
                    <li><Link to="/expert" onClick={handleProfileDropdownLinkClick}>전문가 서비스</Link></li>
                    <li>
                        <a
                            onClick={handleLogoutClick}
                            style={{ color: 'inherit' }}
                        >
                            로그아웃
                        </a>
                    </li>
                </ul>
            </>
        )}
    </div>
    );
};

const getMobileGnbActiveClass = (to, pathname, search) => {
    const [path, queryString] = to.split('?');

    if (path === '/community') {
        const communityPaths = [
            '/community',
            '/free-community',
            '/anon-community',
            '/information',
            '/qna',
            '/program',
            '/notice',
        ];
        return communityPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ? 'active' : '';
    }
    if (path === '/expert') {
        return pathname.startsWith('/expert') ? 'active' : '';
    }
    if (path === '/consulting') {
        return pathname.startsWith('/consulting') ? 'active' : '';
    }
    if (path === '/membership') {
        return pathname.startsWith('/membership') ? 'active' : '';
    }
    if (!pathname.startsWith(path)) {
        return '';
    }
    if (queryString) {
        const expected = new URLSearchParams(queryString);
        const current = new URLSearchParams(search);
        for (const [key, value] of expected.entries()) {
            const currentValue = current.get(key);
            if (currentValue && currentValue !== value) return '';
        }
    }
    return 'active';
};

const MOBILE_GNB_MENU = [
    { to: '/community', text: '커뮤니티', highlight: true },
    { to: '/expert', text: '전문가 서비스', highlight: true },
    { to: '/consulting', text: '마케팅 서비스', highlight: true },
    { to: '/free-community?postType=PT011', text: '자유광장' },
    { to: '/anon-community?postType=PT010', text: '익명광장' },
    { to: '/information?postType=PT020', text: '정보광장' },
    { to: '/qna?postType=PT030', text: '질문답변' },
    { to: '/program?postType=PT040', text: '프로그램' },
    { to: '/notice?postType=PT060', text: '공지' },
    { to: '/membership', text: '멤버쉽' },
];

const MobileGnb = ({ isOpen, onClose, onNavLinkClick }) => {
    const location = useLocation();

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
    <div
        className={`header-mobile-gnb${isOpen ? ' open' : ''}`}
        id="mobileMenu"
        onClick={handleBackdropClick}
    >
        <div className="mobile-gnb">
            <div className="mobile-gnb-head">
                <button type="button" id="gnbCloseBtn" onClick={onClose}>
                    <i className="icon icon-black icon-close"></i>
                </button>
            </div>
            <ul className="mobile-gnb-menu">
                {MOBILE_GNB_MENU.map((item) => (
                    <li
                        key={item.to}
                        className={getMobileGnbActiveClass(item.to, location.pathname, location.search)}
                    >
                        <Link
                            to={item.to}
                            onClick={onNavLinkClick}
                            className={item.highlight ? 'red bold' : ''}
                        >
                            {item.text}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    </div>
    );
};


const MyPageHeader = () => {
    return (
        <div className="header-sub-bar pc-only">
            <div className="vw-inner">
                <div className="header-sub-menu header-sub-menu-center">
                    <ul>
                        <li className="active">
                            <Link to="/mypage/info">내정보</Link>
                        </li>
                        <li>
                            <Link to="/mypage/group">그룹관리</Link>
                        </li>
                        <li>
                            <Link to="/mypage/order">주문내역</Link>
                        </li>
                        <li>
                            <Link to="/mypage/point">포인트내역</Link>
                        </li>
                        <li>
                            <Link to="/mypage/post">게시글내역</Link>
                        </li>
                        <li>
                            <Link to="/mypage/expert">전문가서비스</Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    )
}

const LineBanner = () => {
    const [isOpen, setIsOpen] = useState(true);

    const handleClose = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
    };

    if (!isOpen) return null;

    return (
        <div className="header-banner">
            <Link to="/marketingCompare" className="header-banner__link">
                <p><strong>마케팅 의뢰</strong>하고 <strong>비교견적 무료</strong>로 받으세요!</p>
                <img src="/static/img/assets/banner_arrow.png" alt="" />
            </Link>
            <button type="button" className="icon_btn" onClick={handleClose}>
                <i className="icon icon-close-sm"></i>
            </button>
        </div>
    )
}

const Header = props => {
    const location = useLocation();
    const isMyPage = location.pathname.includes('/mypage');
    const navigate = useNavigate();
    const { user, setUser } = useContext(UserContext);
    const isLoggedIn = Boolean(user?.userId);
    const { unreadChat, openRequests } = useUnreadChatCount(isLoggedIn);
    const [totalSearchKeyword, setTotalSearchKeyword] = useState('');
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [isUserCardOpen, setIsUserCardOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const profileDropdownRef = useRef(null);
    const handleTotalSearchInput = (e) => setTotalSearchKeyword(e.target.value);
    const handleTotalSearchBtn = () => {
        const newSearchKeyword = totalSearchKeyword.trim();
        window.document.dispatchEvent(new CustomEvent(
            'totalSearch', 
            { detail : { totalSearchKeyword: newSearchKeyword }}
        ));
    }
    const handleTotalSearchInputKeyup = e => {
        if (e.which !== 13) return;
        handleTotalSearchBtn();
    }

    const handleContactBtn = () => {
        const url = "http://pf.kakao.com/_QIxlTK";
        const newWindow = window.open(url, "_blank");

        if (newWindow) {
            newWindow.focus();
        } else {
            alert("팝업 차단 설정이 활성화되어 새 창을 열 수 없습니다.");
        }
    }

    const handleLoginBtnClick = () => {
        if (user && user.userId) {
            setIsUserCardOpen(!isUserCardOpen);
        } else {
            navigate('/login');
        }
    }

    const handleToggleNav = () => {
        setIsNavOpen(!isNavOpen);
    }

    const handleNavLinkClick = () => {
        setIsNavOpen(false);
        setIsUserCardOpen(false);
    }

    const handleLikeBtnClick = () => {
        navigate('/expert?isFavoriteOnly=true');
    }

    const openChatService = (tab) => {
        const base = CHAT_SERVICE_URL.replace(/\/$/, '');
        const url = tab ? `${base}/?tab=${tab}` : base;
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
            newWindow.focus();
        } else {
            alert('팝업 차단 설정이 활성화되어 새 창을 열 수 없습니다.');
        }
    }

    const handleLogoutClick = async (e) => {
        e?.preventDefault?.();
        try {
            const rslt = await UserApi.logout();
            setUser({});
            setIsProfileDropdownOpen(false);
            if (rslt?.message) alert(rslt.message);
            navigate('/');
        } catch (err) {
            console.error('logout failed', err);
            alert('로그아웃에 실패했습니다. 잠시후 다시 시도해주세요.');
        }
    };

    const handleProfileMenuToggle = () => {
        setIsProfileDropdownOpen(prev => !prev);
    };

    const handleProfileDropdownLinkClick = () => {
        setIsProfileDropdownOpen(false);
    };

    useEffect(() => {
        setIsProfileDropdownOpen(false);
        setIsNavOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!isProfileDropdownOpen) return;

        const handleClickOutside = (e) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
                setIsProfileDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isProfileDropdownOpen]);
    
    if (window.location.hash === '#/login') return null;

    return (
        <>
            <header className="vh-header-wrap">
                <LineBanner />
                <div className="header-bar">
                    <div className="vw-inner">
                        <MobileGnbOpenBtn onClick={handleToggleNav} />

                        <Link to="/" className="header-logo">
                            <img src="/static/img/logo/new-logo.png" />
                        </Link>

                        <ul className="header-gnb pc-only">
                            {GNB_ITEMS.map((item) => (
                                <li
                                    key={item.to}
                                    className={getGnbActiveClass(item.to, location.pathname)}
                                >
                                    <Link to={item.to} className={item.linkClassName}>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <div className="header-tool">
                            {/* <div className="global-search pc-only">
                                <input type="search" name="" placeholder="통합검색" />
                                <button type="button">
                                    <i className="icon icon-search"></i>
                                </button>
                            </div> */}
                            {!isLoggedIn && (
                                <div className="header-logout-menu">
                                    <Link to="/notice?postType=PT060">이용안내</Link>
                                    <Link to="/login">로그인</Link>
                                </div>
                            )}
                            {isLoggedIn && (
                            <div className="header-login-menu pc-only">
                                <button type="button" className="icon_btn" onClick={() => openChatService('requests')}>
                                    <i className="icon icon-message-lg"></i>
                                    {openRequests > 0 && (
                                        <span className="badge badge_red">
                                            {openRequests > 99 ? '99+' : openRequests}
                                        </span>
                                    )}
                                </button>
                                <button type="button" className="icon_btn" onClick={() => openChatService('chat')}>
                                    <i className="icon icon-bubble-lg"></i>
                                    {unreadChat > 0 && (
                                        <span className="badge badge_red">
                                            {unreadChat > 99 ? '99+' : unreadChat}
                                        </span>
                                    )}
                                </button>
                                <button type="button" className="icon_btn" onClick={handleLikeBtnClick}>
                                    <i className="icon icon-heart-lg"></i>
                                </button>

                                <div className="header-login-widget" ref={profileDropdownRef}>
                                    <a
                                        className="header-login-profile profileMenuBtn"
                                        onClick={handleProfileMenuToggle}
                                    >
                                        <img src={resolveUserProfileImg(user)} alt="" />
                                    </a>
                                    <ul className={`header-login-dropdown${isProfileDropdownOpen ? ' open' : ''}`}>
                                        <li><Link to="/mypage/info" onClick={handleProfileDropdownLinkClick}>마이페이지</Link></li>
                                        <li><Link to="/mypage/post" onClick={handleProfileDropdownLinkClick}>게시글 내역</Link></li>
                                        <li><Link to="/mypage/expert" onClick={handleProfileDropdownLinkClick}>전문가 서비스</Link></li>
                                        <li>
                                            <a href="#" onClick={handleLogoutClick} style={{ color: 'inherit' }}>로그아웃</a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            )}
                            <HeaderWrite />
                        </div>
                        <MobileHeaderTool isLoggedIn={isLoggedIn} onLogout={handleLogoutClick} />
                    </div>
                </div>
                <div className="header-sub-bar pc-only">
                    <div className="vw-inner">
                        <div className={`header-sub-menu ${isMyPage ? 'header-sub-menu-center' : ''}`}>
                            <ul>
                                {getCommunityMenus(isMyPage).map(menu => (
                                    <li key={menu.to} className={linkActiveClass(menu.to)}><Link to={menu.to}>{menu.text}</Link></li>
                                ))}
                            </ul>
                        </div>
                        {!isMyPage && <PopularSearchList />}
                    </div>
                </div>
                <MobileGnb
                    isOpen={isNavOpen}
                    onClose={handleNavLinkClick}
                    onNavLinkClick={handleNavLinkClick}
                />
            </header>
            <div className="header-mobile-sub-menu mo-only">
                <ul className={isMyPage ? 'header-sub-menu-center' : ''}>
                    {getCommunityMenus(isMyPage, true).map((menu) => (
                        <li key={menu.to} className={isMyPage ? linkActiveClass(menu.to) : getGnbActiveClass(menu.to, location.pathname)}>
                            <Link to={menu.to} onClick={handleNavLinkClick}>{menu.text}</Link>
                        </li>
                    ))}
                </ul>
            </div>

        </>
    )
}

export default Header;
// <header className="vh-header-wrap">
//                 <div className="header-bar">
//                     <div className="vw-inner">
//                         <a className="header-logo" onClick={() => navigate('/')}>
//                             <img src="/static/img/logo/new-logo.png" />
//                         </a>

//                         <div className="header-tool">
//                             {/* <div className="global-search">
//                                 <input
//                                     type="text"
//                                     placeholder="검색어를 입력하세요"
//                                     value={totalSearchKeyword}
//                                     onChange={handleTotalSearchInput}
//                                     onKeyUp={handleTotalSearchInputKeyup}
//                                 />
//                                 <button type="button" onClick={handleTotalSearchBtn}>
//                                     <i className="icon icon-search"></i>
//                                 </button>
//                             </div> */}
//                             <a className="icon-btn" onClick={handleLikeBtnClick}>
//                                 <i className="icon icon-heart-fill" style={{fontSize: 24}}></i>
//                             </a>
//                             <a className="btn btn-primary" onClick={handleContactBtn}>
//                                 <span>상담하기</span>
//                                 <i className="icon icon-white icon-arrow-right"></i>
//                             </a>
//                         </div>
//                         <div className="header-mobile-tool">
//                             <button type="button" className="btn" onClick={handleContactBtn}>
//                                 <i className="fas fa-comments"></i> 
//                                 <span>상담하기</span>
//                             </button>
//                             <button type="button" onClick={handleLoginBtnClick}>
//                                 <i className="icon icon-user"></i>
//                             </button>
//                             <button type="button" onClick={handleToggleNav}>
//                                 <i className="icon icon-black icon-bugger"></i>
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//                 <nav className="header-gnb">
//                     <div className="vw-inner">
//                         <ul className="header-gnb-menu">
//                             <Nav handleNavLinkClick={handleNavLinkClick}/>
//                         </ul>
//                     </div>
//                 </nav>
//                 {isNavOpen && <div className="header-mobile-gnb open">
//                     <div className="mobile-gnb">
//                         <div className="mobile-gnb-head">
//                             <button type="button" onClick={handleToggleNav} >
//                                 <i className="icon icon-black icon-close"></i>
//                             </button>
//                         </div>
//                         <ul className='mobile-gnb-menu'>
//                             <Nav type='mobile' handleNavLinkClick={handleNavLinkClick}/>
//                         </ul>
//                     </div>
//                 </div>}
//             </header>
//             { (user.userId && isUserCardOpen) && <UserCard isMobile={true} />}